import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import NumberCommas from "../../Components/NumberCommas/NumberCommas";
import DeleteModal from "../../Components/Common/DeleteModal";
import ExpenseTxnServices from "../../Services/QRCodeServices/ExpenseTxnServices/ExpenseTxnServices";
import ExpenseTxnAddEditModal from "./Components/ExpenseTxnAddEditModal";

function ExpenseTxnList() {
  const [expenseTxns, setExpenseTxns] = useState([]);
  const [filteredTxns, setFilteredTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
    },

    {
      name: "Transaction Date",
      selector: (row) => row.valueDate?.slice(0, 10),
      sortable: true,
    },
    

    {
      name: "Amount",
      selector: (row) => row.transacAmount,
      sortable: true,
      cell: (row) => (
        <div className="w-100 text-end">
          <NumberCommas number={row.transacAmount} />
        </div>
      ),
      width: "120px",
      right: true,
    },

    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="hstack gap-2">
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line"></i>
          </Button>
         
        </div>
      ),
    },
  ];

  async function fetchExpenseTxns() {
    try {
      setLoading(true);
      const fetchedTxns = await ExpenseTxnServices.getList(); // Hypothetical service call
      setExpenseTxns(fetchedTxns);
      setFilteredTxns(fetchedTxns);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching expense transactions:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchExpenseTxns();
  }, []);

  useEffect(() => {
    let filtered = expenseTxns;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((txn) =>
        txn.title?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredTxns(filtered);
  }, [searchTerm, expenseTxns]);

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedTxn(row);
    toggleModal();
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedTxn(null);
    toggleModal();
  };

  const handleModalSuccess = () => {
    fetchExpenseTxns();
    toggleModal();
  };



  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await ExpenseTxnServices.delete(selectedDeleteItem.id); // Hypothetical delete method
        toast.success("Expense transaction deleted successfully");
        fetchExpenseTxns();
      } catch (error) {
        toast.error("Failed to delete expense transaction");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Expense Transactions List"
            pageTitle="Expense Transactions"
          />
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <FormGroup className="col-lg-3">
                      <Input
                        type="text"
                        placeholder="Search by Title"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                    <div className="ms-auto">
                      <Button color="primary" onClick={handleCreate}>
                        + Create Expense Transaction
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredTxns}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ExpenseTxnAddEditModal
        isOpen={modal}
        toggle={toggleModal}
        isEdit={isEdit}
        selectedTxn={selectedTxn}
        onSuccess={handleModalSuccess}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
    </React.Fragment>
  );
}

export default ExpenseTxnList;
